import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, formatApiError, fileSrc } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { StatusBadge } from "@/components/StatusBadge";
import { VehicleFields } from "@/pages/ClientDetail";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Car, ArrowLeft, Pencil, Trash2, Loader2, User, CalendarDays, FileText, Receipt, Wallet, ShieldCheck, Plus, ClipboardList } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";

const eur = (n) => new Intl.NumberFormat("fr-FR", { style: "currency", currency: "EUR" }).format(n || 0);
const PROT_TYPES = [
  ["cire", "Cire"], ["sealant", "Sealant"], ["protection_12_mois", "Protection 12 mois"], ["ceramique_carrosserie", "Céramique carrosserie"],
  ["ceramique_vitres", "Céramique vitres"], ["traitement_cuir", "Traitement cuir"], ["traitement_textile", "Traitement textile"],
  ["protection_capote", "Protection capote"], ["autre", "Autre"],
];
const PROT_LABEL = Object.fromEntries(PROT_TYPES);
const PROT_STATUS = { active: ["Active", "text-emerald-700 bg-emerald-50 border-emerald-200"], bientot: ["Bientôt à renouveler", "text-amber-700 bg-amber-50 border-amber-200"], expiree: ["Expirée", "text-red-700 bg-red-50 border-red-200"] };

const fmtDate = (iso) => (iso ? new Date(iso).toLocaleString("fr-FR", { dateStyle: "medium", timeStyle: "short" }) : "-");

export default function VehicleDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [vehicle, setVehicle] = useState(null);
  const [editOpen, setEditOpen] = useState(false);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/vehicles/${id}`);
      setVehicle(data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/vehicles/${id}`, {
        client_id: vehicle.client_id, make: form.make, model: form.model, version: form.version,
        year: form.year, plate: form.plate, color: form.color, mileage: form.mileage,
        vehicle_type: form.vehicle_type, notes: form.notes, photos: form.photos || [],
      });
      toast.success("Véhicule mis à jour");
      setEditOpen(false);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setSaving(false); }
  };

  const del = async () => {
    try { await api.delete(`/vehicles/${id}`); toast.success("Véhicule supprimé"); navigate("/vehicles"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  if (!vehicle) return <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-emerald-600" /></div>;

  const collectPhotos = [
    ...(vehicle.photos || []),
    ...(vehicle.appointments || []).flatMap((a) => [...(a.before_photos || []), ...(a.after_photos || [])]),
  ];

  return (
    <div>
      <Button variant="ghost" onClick={() => navigate("/vehicles")} className="mb-4 text-slate-500 hover:text-slate-900" data-testid="btn-back">
        <ArrowLeft className="w-4 h-4 mr-1.5" /> Retour aux véhicules
      </Button>

      <PageHeader
        title={`${vehicle.make} ${vehicle.model || ""}`}
        subtitle={[vehicle.year, vehicle.plate].filter(Boolean).join(" · ")}
        icon={Car}
        actions={
          <>
            <Button variant="outline" onClick={() => { setForm(vehicle); setEditOpen(true); }} data-testid="btn-edit-vehicle" className="border-slate-200 bg-white"><Pencil className="w-4 h-4 mr-1.5" /> Modifier</Button>
            <AlertDialog>
              <AlertDialogTrigger asChild><Button variant="outline" data-testid="btn-delete-vehicle" className="border-red-500/30 bg-red-500/5 text-red-600 hover:bg-red-500/10"><Trash2 className="w-4 h-4" /></Button></AlertDialogTrigger>
              <AlertDialogContent className="bg-white border-slate-200">
                <AlertDialogHeader><AlertDialogTitle className="text-slate-900">Supprimer ce véhicule ?</AlertDialogTitle><AlertDialogDescription>Action irréversible.</AlertDialogDescription></AlertDialogHeader>
                <AlertDialogFooter><AlertDialogCancel className="bg-transparent border-slate-200">Annuler</AlertDialogCancel><AlertDialogAction onClick={del} className="bg-red-500 hover:bg-red-600">Supprimer</AlertDialogAction></AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </>
        }
      />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="bg-white border border-slate-200 rounded-xl p-5 space-y-2 text-sm">
          <Row label="Marque" value={vehicle.make} />
          <Row label="Modèle" value={vehicle.model} />
          <Row label="Version" value={vehicle.version} />
          <Row label="Année" value={vehicle.year} />
          <Row label="Immatriculation" value={vehicle.plate} />
          <Row label="Couleur" value={vehicle.color} />
          <Row label="Kilométrage" value={vehicle.mileage} />
          <Row label="Type" value={vehicle.vehicle_type} />
          {vehicle.client && (
            <button onClick={() => navigate(`/clients/${vehicle.client.id}`)} className="flex items-center gap-2 text-emerald-600 hover:underline pt-2 border-t border-slate-200 w-full" data-testid="link-vehicle-client">
              <User className="w-4 h-4" /> {vehicle.client.first_name} {vehicle.client.last_name}
            </button>
          )}
          {vehicle.notes && <div className="text-slate-700 pt-2 border-t border-slate-200">{vehicle.notes}</div>}
        </div>

        <div className="lg:col-span-2 space-y-6">
          <div className="bg-white border border-slate-200 rounded-xl p-5">
            <h3 className="font-heading text-base font-semibold text-slate-900 mb-3 flex items-center gap-2"><CalendarDays className="w-4 h-4 text-cyan-600" /> Historique des rendez-vous</h3>
            {vehicle.appointments?.length ? (
              <div className="space-y-2">
                {vehicle.appointments.map((a) => (
                  <div key={a.id} className="flex items-center justify-between bg-slate-50 border border-slate-200 rounded-lg p-3">
                    <div><div className="text-sm text-slate-900">{a.title || "RDV"}</div><div className="text-xs text-slate-500">{fmtDate(a.start)}{a.price != null ? ` · ${eur(a.price)}` : ""}</div></div>
                    <StatusBadge status={a.status} />
                  </div>
                ))}
              </div>
            ) : <p className="text-sm text-slate-500">Aucun rendez-vous.</p>}
          </div>

          <ProtectionsCard vehicle={vehicle} onChange={load} />

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            <div className="bg-white border border-slate-200 rounded-xl p-5">
              <h3 className="font-heading text-sm font-semibold text-slate-900 mb-3 flex items-center gap-2"><FileText className="w-4 h-4 text-indigo-600" /> Devis</h3>
              {vehicle.quotes?.length ? vehicle.quotes.map((q) => (
                <div key={q.id} className="flex items-center justify-between text-sm py-1.5 border-b border-slate-100 last:border-0">
                  <span className="text-slate-700">{q.number}</span><span className="text-slate-500">{eur(q.total)}</span>
                </div>
              )) : <p className="text-sm text-slate-500">Aucun devis.</p>}
            </div>
            <div className="bg-white border border-slate-200 rounded-xl p-5">
              <h3 className="font-heading text-sm font-semibold text-slate-900 mb-3 flex items-center gap-2"><Receipt className="w-4 h-4 text-emerald-600" /> Factures</h3>
              {vehicle.invoices?.length ? vehicle.invoices.map((i) => (
                <div key={i.id} className="flex items-center justify-between text-sm py-1.5 border-b border-slate-100 last:border-0">
                  <span className="text-slate-700">{i.number}</span><span className="text-slate-500">{eur(i.total)}</span>
                </div>
              )) : <p className="text-sm text-slate-500">Aucune facture.</p>}
            </div>
          </div>

          {vehicle.payments?.length > 0 && (
            <div className="bg-white border border-slate-200 rounded-xl p-5">
              <h3 className="font-heading text-sm font-semibold text-slate-900 mb-3 flex items-center gap-2"><Wallet className="w-4 h-4 text-teal-600" /> Paiements</h3>
              {vehicle.payments.map((p) => (
                <div key={p.id} className="flex items-center justify-between text-sm py-1.5 border-b border-slate-100 last:border-0">
                  <span className="text-slate-700">{fmtDate(p.date)} · {p.method}</span><span className="text-slate-500">{eur(p.amount)}</span>
                </div>
              ))}
            </div>
          )}

          {vehicle.inspections?.length > 0 && (
            <div className="bg-white border border-slate-200 rounded-xl p-5">
              <h3 className="font-heading text-sm font-semibold text-slate-900 mb-3 flex items-center gap-2"><ClipboardList className="w-4 h-4 text-slate-600" /> États des lieux</h3>
              {vehicle.inspections.map((ins) => (
                <div key={ins.id} className="text-sm py-2 border-b border-slate-100 last:border-0">
                  <div className="text-slate-700">{fmtDate(ins.created_at)} · {ins.general_state || "—"} · {ins.mileage || "?"} km</div>
                  {ins.defects?.length ? <div className="text-xs text-amber-700">{ins.defects.join(", ")}</div> : null}
                </div>
              ))}
            </div>
          )}

          <div className="bg-white border border-slate-200 rounded-xl p-5">
            <h3 className="font-heading text-base font-semibold text-slate-900 mb-3">Photos</h3>
            {collectPhotos.length ? (
              <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
                {collectPhotos.map((p, i) => (
                  <a key={i} href={fileSrc(p.url || p.path)} target="_blank" rel="noreferrer" className="aspect-square rounded-lg overflow-hidden border border-slate-200">
                    <img src={fileSrc(p.url || p.path)} alt="" className="w-full h-full object-cover" />
                  </a>
                ))}
              </div>
            ) : <p className="text-sm text-slate-500">Aucune photo.</p>}
          </div>
        </div>
      </div>

      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="bg-white border-slate-200 max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="text-slate-900">Modifier le véhicule</DialogTitle></DialogHeader>
          <VehicleFields form={form} setForm={setForm} />
          <DialogFooter>
            <Button variant="ghost" onClick={() => setEditOpen(false)}>Annuler</Button>
            <Button onClick={save} disabled={saving} data-testid="btn-save-vehicle-edit" className="bg-emerald-500 hover:bg-emerald-600 text-black font-semibold">{saving && <Loader2 className="w-4 h-4 animate-spin mr-2" />}Enregistrer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Row({ label, value }) {
  if (!value) return null;
  return <div className="flex justify-between"><span className="text-slate-500">{label}</span><span className="text-slate-700">{value}</span></div>;
}

function ProtectionsCard({ vehicle, onChange }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ type: "ceramique_carrosserie", product: "", posed_at: new Date().toISOString().slice(0, 10), duration_months: 12, notes: "" });
  const [saving, setSaving] = useState(false);
  const prots = vehicle.protections || [];

  const add = async () => {
    setSaving(true);
    try {
      await api.post("/protections", { vehicle_id: vehicle.id, client_id: vehicle.client_id, type: form.type, product: form.product || null, posed_at: form.posed_at, duration_months: Number(form.duration_months) || 12, notes: form.notes || null });
      toast.success("Protection ajoutée");
      setOpen(false);
      setForm({ type: "ceramique_carrosserie", product: "", posed_at: new Date().toISOString().slice(0, 10), duration_months: 12, notes: "" });
      onChange && onChange();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setSaving(false); }
  };

  const del = async (id) => { try { await api.delete(`/protections/${id}`); onChange && onChange(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } };

  return (
    <div className="bg-white border border-slate-200 rounded-xl p-5" data-testid="protections-card">
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-heading text-base font-semibold text-slate-900 flex items-center gap-2"><ShieldCheck className="w-4 h-4 text-emerald-600" /> Protections / traitements</h3>
        <Button size="sm" variant="outline" onClick={() => setOpen((o) => !o)} data-testid="btn-add-protection" className="border-slate-200"><Plus className="w-4 h-4 mr-1" /> Ajouter</Button>
      </div>
      {open && (
        <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 mb-3 space-y-3" data-testid="protection-form">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5"><Label className="text-xs text-slate-500">Type</Label>
              <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
                <SelectTrigger data-testid="protection-type" className="bg-white border-slate-200"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-white border-slate-200">{PROT_TYPES.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5"><Label className="text-xs text-slate-500">Durée (mois)</Label><Input type="number" min="1" data-testid="protection-duration" value={form.duration_months} onChange={(e) => setForm({ ...form, duration_months: e.target.value })} className="bg-white border-slate-200" /></div>
            <div className="space-y-1.5"><Label className="text-xs text-slate-500">Date de pose</Label><Input type="date" data-testid="protection-posed" value={form.posed_at} onChange={(e) => setForm({ ...form, posed_at: e.target.value })} className="bg-white border-slate-200" /></div>
            <div className="space-y-1.5"><Label className="text-xs text-slate-500">Produit (optionnel)</Label><Input data-testid="protection-product" value={form.product} onChange={(e) => setForm({ ...form, product: e.target.value })} className="bg-white border-slate-200" /></div>
          </div>
          <Textarea placeholder="Notes" data-testid="protection-notes" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="bg-white border-slate-200" rows={2} />
          <Button size="sm" onClick={add} disabled={saving} data-testid="btn-save-protection" className="bg-emerald-500 hover:bg-emerald-600 text-black font-semibold">{saving && <Loader2 className="w-4 h-4 animate-spin mr-1.5" />}Enregistrer</Button>
        </div>
      )}
      {prots.length ? (
        <div className="space-y-2" data-testid="protections-list">
          {prots.map((p) => {
            const [lbl, cls] = PROT_STATUS[p.status] || PROT_STATUS.active;
            return (
              <div key={p.id} className="flex items-center justify-between bg-slate-50 border border-slate-200 rounded-lg p-3">
                <div className="min-w-0">
                  <div className="text-sm text-slate-900">{PROT_LABEL[p.type] || p.type}{p.product ? ` · ${p.product}` : ""}</div>
                  <div className="text-xs text-slate-500">Posé le {fmtDate(p.posed_at)} · échéance {fmtDate(p.expiry_date)}</div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <span className={`text-[10px] px-2 py-0.5 rounded-full border ${cls}`}>{lbl}</span>
                  <button onClick={() => del(p.id)} data-testid={`btn-delete-protection-${p.id}`} className="text-slate-400 hover:text-red-600"><Trash2 className="w-3.5 h-3.5" /></button>
                </div>
              </div>
            );
          })}
        </div>
      ) : <p className="text-sm text-slate-500">Aucune protection enregistrée.</p>}
    </div>
  );
}
